/**
 * /portal/chat — Tracker chat-as-hero route.
 *
 * Search-param schema:
 *   - `threadId?`  — deep-link to a persisted thread (W5; sidebar
 *     Conversations row click). When present, the page rehydrates
 *     via `useChatThreadQuery` and swaps the message buffer.
 *   - `seedPrompt?` — pre-formatted question seeded into the
 *     composer when the user arrives from an external entry point
 *     (Wave-10 row-chat icon, LL152 "Schedule wave" button,
 *     scheduling success-card chips, dashboard "Ask Clear").
 *   - `seedRowId?` — BE-stamped row identifier (Wave-11 contract
 *     `${issuer_prefix}:${record_locator}`, e.g. `"ecb:12345678X"`)
 *     echoed back from the penalty-composer wire. Carried into the
 *     BE `row_explain` fast-path so it can resolve the row from the
 *     building's penalty snapshot. The FE never invents this value.
 *   - `seedBuildingId?` — building UUID the row belongs to. Paired
 *     with `seedRowId` on the chat-turn metadata so the BE
 *     `row_explain(row_id, building_id)` fast-path resolves the
 *     row from a building-scoped snapshot (Wave-11 Tracks G+H).
 *     Independent of the existing `buildingId` chat-turn arg used
 *     by the BuildingPickerPopover — that field scopes the *thread*;
 *     this one scopes the *seed turn only*. Also drives the
 *     Discussing strip's "← Back to building" CTA.
 *   - `autoSend?` — `"1"` fires the seed prompt immediately on
 *     mount; absent leaves it in the composer for the user to
 *     review before pressing Enter.
 *
 * Mirrors the `buildings/$buildingId` route's lightweight
 * `validateSearch` pattern — no zod dependency just for a few
 * optional string fields.
 *
 * Wave-17 Item 1.1 — the page component is lazy-loaded so chat's
 * chat-tools imports (KaTeX, react-markdown, the whole chat surface)
 * do not land in the cold-load main chunk. Search-param validation
 * stays eager so the router can hydrate the route without paying for
 * the chat bundle.
 */
import { createFileRoute, lazyRouteComponent } from "@tanstack/react-router";

export type PortalChatSearch = {
  threadId?: string;
  seedPrompt?: string;
  seedRowId?: string;
  seedBuildingId?: string;
  /**
   * LL87 chat-intro (FR-CI-9) — the planner's ticked building UUIDs as
   * ONE comma-joined string (URL-stable; the page splits it and ships
   * the list as structured `seed_building_ids` turn metadata). The
   * selection never rides in the seed prompt text.
   */
  seedBuildingIds?: string;
  autoSend?: "1";
};

const readString = (v: unknown): string | undefined =>
  typeof v === "string" && v.length > 0 ? v : undefined;

/**
 * Exported separately from the {@link Route} declaration so unit tests
 * can pin the parser contract (presence/absence of each field, the
 * `autoSend === "1"` narrowing) without having to spin up a full
 * TanStack router instance just to read `Route.options.validateSearch`.
 */
export const validatePortalChatSearch = (
  search?: Record<string, unknown>,
): PortalChatSearch => {
  const autoSendRaw = search?.autoSend;
  return {
    threadId: readString(search?.threadId),
    seedPrompt: readString(search?.seedPrompt),
    seedRowId: readString(search?.seedRowId),
    seedBuildingId: readString(search?.seedBuildingId),
    seedBuildingIds: readString(search?.seedBuildingIds),
    autoSend: autoSendRaw === "1" ? "1" : undefined,
  };
};

export const Route = createFileRoute("/portal/chat")({
  component: lazyRouteComponent(() => import("@/pages/Portal/Chat/Page")),
  validateSearch: validatePortalChatSearch,
});
